import { toFixed } from "@shared/utils/numbers";
import { useCallback, useEffect, useState } from "react";
import { TradeAmountData } from "@shared/features/trades/EditTrade/types";
import { getConversionMinCost } from "@shared/features/trades/common/utils";
import { sharedLib } from "@shared/services/shared.service";
import { PrecisionConfig } from "@shared/types";

interface UseTradeAmountsProps {
  symbol?: string;
  accountId?: number;
  skip?: boolean;
  precisionConfig?: PrecisionConfig;
  leverage: number;
  currentPrice: number;
  defaultAmount?: number;
  defaultQuantity?: number;
  reverse: boolean;
}

const useTradeAmounts = ({
  symbol,
  accountId,
  precisionConfig,
  skip,
  leverage,
  currentPrice,
  defaultAmount,
  defaultQuantity,
  reverse,
}: UseTradeAmountsProps) => {
  const [tradeAmount, setTradeAmount] = useState<number>(defaultAmount || 0);
  const [tradeQuantity, setTradeQuantity] = useState<number>(
    defaultQuantity || 0
  );
  const { data: availableAmountData, isFetching: availableAmountLoading } =
    sharedLib.sharedApi.common.useGetAvailableAmountQuery(
      { symbol: symbol || "", account_id: accountId || 0, reverse },
      {
        refetchOnMountOrArgChange: true,
        skip: skip || !accountId || !symbol,
      }
    );
  const { data: availableQuantityData, isFetching: availableQuantityLoading } =
    sharedLib.sharedApi.common.useGetAvailableQuantityQuery(
      { symbol: symbol || "", account_id: accountId || 0, reverse },
      {
        refetchOnMountOrArgChange: true,
        skip: skip || !accountId || !symbol,
      }
    );

  const resetAmountChanges = () => {
    setTradeAmount(defaultAmount || 0);
    setTradeQuantity(defaultQuantity || 0);
  };

  useEffect(() => {
    resetAmountChanges();
  }, [defaultAmount]);

  const amountPrecision =
    precisionConfig?.amount || availableAmountData?.precision.amount || 0;
  const quantityPrecision = precisionConfig?.quantity || 0;
  const availableAmount = toFixed(
    availableAmountData?.data?.balance?.amount || 0,
    amountPrecision
  );
  const availableQuantity = toFixed(
    availableQuantityData?.data?.balance?.quantity || 0,
    quantityPrecision
  );

  const conversionFormula = availableQuantityData?.amount_conversions;
  const amountLimits = availableAmountData?.limits;
  const quantityLimits = availableQuantityData?.limits;

  const convertAmountToQuantity = (
    amount: number,
    orderPrice: number
  ): number =>
    getConversionMinCost(
      false,
      leverage,
      amountPrecision,
      quantityPrecision,
      orderPrice,
      availableQuantityData?.amount_conversions,
      amount
    );

  const getMinAmountByPrice = useCallback(
    (price: number): number =>
      getConversionMinCost(
        true,
        leverage,
        amountPrecision,
        quantityPrecision,
        price,
        conversionFormula,
        amountLimits?.amount.min,
        quantityLimits?.quantity.min
      ),
    [leverage, amountPrecision, conversionFormula, amountLimits, quantityLimits]
  );

  const minAmount = getMinAmountByPrice(currentPrice);

  const minQuantity = getConversionMinCost(
    false,
    leverage,
    amountPrecision,
    quantityPrecision,
    currentPrice,
    conversionFormula,
    amountLimits?.amount.min,
    quantityLimits?.quantity.min
  );

  const amountData: TradeAmountData = {
    available: availableAmount,
    precision: amountPrecision,
    selected: tradeAmount,
    set: (amount: number) => setTradeAmount(toFixed(amount, amountPrecision)),
    coin: availableAmountData?.coin,
    min: minAmount,
    original: defaultAmount || 0,
    getAvailable: () =>
      toFixed(
        availableAmount - (tradeAmount - (defaultAmount || 0)),
        amountPrecision
      ),
  };

  const quantityData: TradeAmountData = {
    available: Math.abs(availableQuantity),
    precision: quantityPrecision,
    selected: tradeQuantity,
    set: (quantity: number) =>
      setTradeQuantity(toFixed(quantity, quantityPrecision)),
    coin: availableQuantityData?.coin,
    min: minQuantity,
    conversionFormula: availableQuantityData?.amount_conversions,
    original: defaultQuantity || 0,
    getAvailable: () =>
      toFixed(
        availableQuantity - (tradeQuantity - (defaultQuantity || 0)),
        quantityPrecision
      ),
    reverse: availableQuantity < 0,
  };

  return {
    amountData,
    quantityData,
    isLoading: availableAmountLoading || availableQuantityLoading,
    resetAmountChanges,
    convertAmountToQuantity,
    getMinAmountByPrice,
  };
};

export default useTradeAmounts;
